import { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

export default function Register() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("US");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState(() => localStorage.getItem("wg_ref") || "");
  const [refInfo, setRefInfo] = useState(null);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("ref");
    if (q) { localStorage.setItem("wg_ref", q.toUpperCase()); setRef(q.toUpperCase()); }
  }, []);
  useEffect(() => {
    if (!ref) { setRefInfo(null); return; }
    api.get(`/referral/check/${encodeURIComponent(ref)}`).then(({ data }) => setRefInfo(data)).catch(() => setRefInfo({ valid: false }));
  }, [ref]);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agree) { setError("Please agree to the Terms of Service and Privacy Policy to continue."); return; }
    setLoading(true);
    try {
      await register(email, password, name, dob || null, state || null, country || "US", agree, refInfo?.valid ? ref : null);
      localStorage.removeItem("wg_ref");
      nav("/dashboard");
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-md mx-auto px-6 pt-20 pb-24">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-3">Founding Member preorder</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">See it before you spend a cent.</h1>
        <p className="text-moss mt-3">
          Sign up to explore Withdraw Guard with <strong>play money</strong>. You&apos;ll be
          able to reserve your Founding Member membership for $45 — Withdraw Guard is $45/month, and this covers your first month, with no further charge until your membership begins.
        </p>

        {refInfo?.valid && (
          <div className="mt-6 rounded-xl border border-gold/50 bg-gold/10 px-4 py-3 text-sm text-ink" data-testid="register-referral-banner">
            <strong>{refInfo.referrer_first_name}</strong> invited you — join and get <strong>${refInfo.friend_reward}</strong> in Spending when you become a member. Terms apply.
          </div>
        )}
        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-form">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Full name</label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
              className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-sans focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
              data-testid="register-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-sans focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
              data-testid="register-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Password</label>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-sans focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
              data-testid="register-password" />
            <p className="mt-1.5 text-xs text-sage">Minimum 6 characters.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Date of birth</label>
            <input type="date" required value={dob} onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0,10)}
              className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-sans focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
              data-testid="register-dob" />
            <p className="mt-1.5 text-xs text-sage">Your retirement vault unlocks on your 60th birthday.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Country</label>
              <select required value={country} onChange={(e) => setCountry(e.target.value)}
                className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-sans focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                data-testid="register-country">
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="XX">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">State / Region</label>
              {country === "US" ? (
                <select required value={state} onChange={(e) => setState(e.target.value)}
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-sans focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  data-testid="register-state">
                  <option value="">Pick your state…</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input type="text" required value={state} onChange={(e) => setState(e.target.value)}
                  placeholder="Province / region"
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-sans focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  data-testid="register-state" />
              )}
            </div>
          </div>
          <label className="flex items-start gap-3 text-sm text-moss cursor-pointer select-none" data-testid="register-terms-row">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-sand accent-forest"
              data-testid="register-terms-checkbox" />
            <span>
              I agree to the{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-forest hover:text-forest-deep font-medium underline decoration-gold underline-offset-2" data-testid="register-terms-link">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-forest hover:text-forest-deep font-medium underline decoration-gold underline-offset-2" data-testid="register-privacy-link">Privacy Policy</Link>.
            </span>
          </label>
          {error && <div className="text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="register-error">{error}</div>}
          <button type="submit" disabled={loading || !agree}
            className="w-full bg-forest text-white rounded-lg px-6 py-3.5 font-sans font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
            data-testid="register-submit">
            {loading ? "Creating…" : "Create account + explore the app →"}
          </button>
          <p className="text-[11px] text-sage text-center leading-relaxed">
            By signing up you&apos;ll get full demo access (play money) + the option to preorder for $45 (your first month of $45/mo).
            No card required. No charges.
          </p>
        </form>
        <p className="mt-8 text-sm text-moss">
          Already have an account?{" "}
          <Link to="/login" className="text-forest hover:text-forest-deep font-medium" data-testid="register-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
